import type { ApiManualMeasuresAddition } from '@highimpact-frontend/api-types';
import axios, { AxiosHeaders } from 'axios';
import type { NavigateFunction } from 'react-router-dom';
import { BASE_URL } from '../constants';
import { getSecurityToken } from '../utils/security';
import { eventBus } from './apiEventBus';
import { createTypedAxiosInstance } from './createTypedAxiosInstance';

// Axios factorized configuration
function createAxiosInstance(api_url: string) {
  const instance = axios.create({
    baseURL: `${BASE_URL}${api_url}`,
    headers: {
      Authorization: getSecurityToken() ? `Bearer ${getSecurityToken()}` : null,
    },
  });

  // Always read the freshest token so requests made after login are authenticated.
  instance.interceptors.request.use((config) => {
    const token = getSecurityToken();

    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  });

  return instance;
}

export const api_authentication = createAxiosInstance('/authentication');
export const api_config = createAxiosInstance('/config');
export const api_measures = createAxiosInstance('/measures');
export const api_permissions = createAxiosInstance('/permissions');
export const api_manual_data = createTypedAxiosInstance<
  unknown,
  unknown,
  never,
  ApiManualMeasuresAddition,
  string
>('/data-dashboard/manual_measures');
export const api_semantic = createAxiosInstance('/semantic');
export const api_data_dasboard = createAxiosInstance('/data-dashboard');
export const api_report = createAxiosInstance('/report');
export const api_dataprocessors = createAxiosInstance('/dataprocessors');

export const api_instances = [
  api_authentication,
  api_config,
  api_measures,
  api_semantic,
  api_data_dasboard,
  api_report,
  api_manual_data,
  api_permissions,
  api_dataprocessors,
];

// -- Setup --

export function setupResponseInterceptor(navigate: NavigateFunction) {
  const isStorybookPreview = window.location.pathname.includes('iframe.html');
  console.log(
    `Setting up API response interceptor. Storybook preview mode: ${isStorybookPreview}`
  );
  [
    api_config,
    api_measures,
    api_manual_data,
    api_semantic,
    api_data_dasboard,
    api_report,
    api_permissions,
  ].forEach((api) => {
    api.interceptors.response.use(
      (response) => response,
      (error) => {
        if ([401, 403, 498].includes(error.response.status)) {
          if (
            !window.location.pathname.startsWith('/public/') &&
            !isStorybookPreview
          )
            window.location.href = '/login';
          else {
            eventBus.dispatchEvent(
              new CustomEvent('auth-error', {
                detail: { status: error.response.status },
              })
            );
          }
        }

        return Promise.reject(error);
      }
    );
  });
}
